import React from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useFsFlag } from "@flagship.io/react-sdk";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { Dialog, Stack, Box, Text, Button, Tag } from "@includes";
import { FLAGS_ENUM } from "@/utils/enums/flags";
import { RootState } from "@store";

/**
 * Optional elements of the modal — kept editable so the simplification
 * options can be toggled without touching the layout.
 */
const SHOW_TAG = true; // "NOUVEAU" / "NEW" badge
const SHOW_VIDEO = true; // central video player
const SHOW_VIDEO_CAPTION = true; // caption under the video

// Base Commerce URL — the current siteId is appended at click time.
const COMMERCE_BASE_URL =
  process.env.REACT_APP_COMMERCE_URL || "https://commerce.abtasty.com";
const COMMERCE_VIDEO_URL =
  process.env.REACT_APP_COMMERCE_VIDEO_URL ||
  `${process.env.PUBLIC_URL}/videos/commerce-banner.mp4`;
const COMMERCE_VIDEO_POSTER =
  process.env.REACT_APP_COMMERCE_VIDEO_POSTER || "";

const BENEFITS = [
  {
    key: "navigation",
    Icon: NearMeOutlinedIcon,
    titleKey: "commerce_modal_benefit_navigation_title",
    descKey: "commerce_modal_benefit_navigation_desc",
  },
  {
    key: "catalog",
    Icon: StorageOutlinedIcon,
    titleKey: "commerce_modal_benefit_catalog_title",
    descKey: "commerce_modal_benefit_catalog_desc",
  },
  {
    key: "search",
    Icon: SearchOutlinedIcon,
    titleKey: "commerce_modal_benefit_search_title",
    descKey: "commerce_modal_benefit_search_desc",
  },
  {
    key: "workflow",
    Icon: BoltOutlinedIcon,
    titleKey: "commerce_modal_benefit_workflow_title",
    descKey: "commerce_modal_benefit_workflow_desc",
  },
];

const noop = () => {};

const DiscoverCommerceDialog: React.FC = () => {
  const theme = useTheme();
  const { t }: i18translateType = useTranslation();

  const isLoggedIn = useSelector((state: RootState) => state.login.isLoggedIn);
  const siteId = useSelector((state: RootState) => state.site.siteId);

  // Targeting (incl. the excluded site_id list) is handled in Flagship (FE&R).
  // Defaults to false: the modal is hidden unless the flag is explicitly true.
  const flagShowCommerceModal = useFsFlag(FLAGS_ENUM.SHOW_COMMERCE_MODAL);
  const shouldShow = flagShowCommerceModal.getValue(false);

  if (!isLoggedIn || !shouldShow) return null;

  const openCommerce = () => {
    window.open(
      `${COMMERCE_BASE_URL}/site/${siteId}/`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <Dialog
      open
      onClose={noop}
      PaperProps={{
        sx: {
          width: "min(96vw, 1180px)",
          maxWidth: "min(96vw, 1180px)",
          borderRadius: 2,
        },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={5}
        sx={{ p: { xs: 4, md: 6 } }}
      >
        {/* Left column — text, benefits & CTA */}
        <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
          {SHOW_TAG && (
            <Tag
              tagVariant="primary"
              customStyle={{
                margin: "0 !important",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                width: "fit-content",
              }}
            >
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />
              {t("commerce_modal_tag")}
            </Tag>
          )}

          <Stack spacing={1}>
            <Text variant="h4" customStyle={{ fontWeight: 700, textDecoration: "none" }}>
              {t("commerce_modal_title")}{" "}
              <Box
                component="span"
                sx={{ color: theme.customPalette.bluePurple.main }}
              >
                {t("commerce_modal_title_highlight")}
              </Box>
            </Text>
            <Text
              variant="body2"
              customStyle={{ color: theme.customPalette.grey.light }}
            >
              {t("commerce_modal_subtitle")}
            </Text>
          </Stack>

          <Stack spacing={2}>
            {BENEFITS.map(({ key, Icon, titleKey, descKey }) => (
              <Stack key={key} direction="row" spacing={2} alignItems="start">
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: theme.customPalette.bluePurple.lighter,
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 20,
                      color: theme.customPalette.bluePurple.main,
                    }}
                  />
                </Box>
                <Stack spacing={0.25}>
                  <Text variant="subtitle2" customStyle={{ fontWeight: 700, textDecoration: "none" }}>
                    {t(titleKey)}
                  </Text>
                  <Text
                    variant="body2"
                    customStyle={{ color: theme.customPalette.grey.light }}
                  >
                    {t(descKey)}
                  </Text>
                </Stack>
              </Stack>
            ))}
          </Stack>

          <Box>
            <Button
              btnVariant="primary"
              onClick={openCommerce}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
              id="openCommerceBtn"
            >
              {t("commerce_modal_cta")}
            </Button>
          </Box>
        </Stack>

        {/* Right column — video */}
        {SHOW_VIDEO && (
          <Stack spacing={1.5} sx={{ flex: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                flex: 1,
                width: "100%",
                minHeight: 340,
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: theme.customPalette.bluePurple.darker,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {COMMERCE_VIDEO_URL ? (
                <Box
                  component="video"
                  controls
                  poster={COMMERCE_VIDEO_POSTER || undefined}
                  src={COMMERCE_VIDEO_URL}
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <Text
                  variant="caption"
                  centerText
                  customStyle={{ color: theme.customPalette.grey.disabled }}
                >
                  {/* TODO: provide REACT_APP_COMMERCE_VIDEO_URL */}
                  Video
                </Text>
              )}
            </Box>
            {SHOW_VIDEO_CAPTION && (
              <Text
                variant="caption"
                centerText
                customStyle={{ color: theme.customPalette.grey.light }}
              >
                {t("commerce_modal_video_caption")}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
};

export default DiscoverCommerceDialog;
